import { createContext, useContext, useLayoutEffect, useState } from 'react'

/**
 * Where a dialog sits in the stack of open dialogs.
 *
 * Every dialog now lives on `document.body` - `Modal` portals there, and a `confirm()` dialog gets
 * its own React root - so DOM nesting no longer decides which of two open dialogs paints on top.
 * Order comes from this stack instead: a dialog opened from inside another one sits one level above
 * it, and a dialog with no dialog above it in the React tree takes the next free level.
 *
 * Without it, a dialog opened from a deeper one paints UNDER that one's backdrop: the Email
 * Template picker inside Create Email / Note read as greyed out and ignored every click.
 */
export const ModalStackLevelContext = createContext<number | null>(null)

const claimedLevels: number[] = []

const nextFreeLevel = () => (claimedLevels.length ? Math.max(...claimedLevels) + 1 : 0)

/** Content layer of a dialog. The backdrop sits five below it. Floater tabs clear this number. */
export const modalContentZ = (level: number) => 60 + level * 10

type FloaterLayer = { id: number; z: number; aboveFloater: boolean }

let layerSeq = 1
const floaterLayers: FloaterLayer[] = []
/** Highest dialog a floater tab click asked the panels to cover. */
let coverThroughZ = 0
/** pointerdown inside an open floater panel, so the dialog it raises stays above that panel. */
let floaterPointerAt = 0
let floaterListenersReady = false

const syncFloaterZ = () => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const pageZs = floaterLayers.filter((layer) => !layer.aboveFloater).map((layer) => layer.z)
  const allZs = floaterLayers.map((layer) => layer.z)
  const pageMax = pageZs.length ? Math.max(...pageZs) : 0
  const allMax = allZs.length ? Math.max(...allZs) : 0
  if (coverThroughZ > allMax) coverThroughZ = allMax
  const panelBase = Math.max(pageMax, coverThroughZ)
  if (panelBase <= 0) {
    root.classList.remove('modal-keep-floater-tabs')
    root.style.removeProperty('--modal-floater-tab-z')
    root.style.removeProperty('--modal-floater-panel-z')
    return
  }
  const panelZ = panelBase + 1
  const tabZ = Math.max(allMax, panelZ) + 1
  root.classList.add('modal-keep-floater-tabs')
  root.style.setProperty('--modal-floater-panel-z', String(panelZ))
  root.style.setProperty('--modal-floater-tab-z', String(tabZ))
}

const ensureFloaterListeners = () => {
  if (floaterListenersReady || typeof document === 'undefined') return
  floaterListenersReady = true
  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target
      if (!(target instanceof Element)) return
      floaterPointerAt = target.closest('.floater-panel') ? Date.now() : 0
      if (!target.closest('.floater-tab')) return
      coverThroughZ = floaterLayers.reduce((max, layer) => Math.max(max, layer.z), 0)
      syncFloaterZ()
    },
    true,
  )
}

const openedFromFloaterPanel = () => {
  const recent = Date.now() - floaterPointerAt < 1000
  if (recent) floaterPointerAt = 0
  const focused = !!document.activeElement?.closest('.floater-panel')
  return recent || focused
}

export const useModalStackLevel = (isOpen: boolean) => {
  const parentLevel = useContext(ModalStackLevelContext)
  const [level, setLevel] = useState(0)

  // Claim before paint, so a dialog never shows for a frame at the wrong level.
  // Every open dialog also lifts the loan floater tabs above itself. A dialog opened from
  // inside a floater panel stays above that panel, so its own confirm still receives clicks.
  useLayoutEffect(() => {
    if (!isOpen) return
    ensureFloaterListeners()

    const claimed = parentLevel === null ? nextFreeLevel() : parentLevel + 1
    claimedLevels.push(claimed)
    setLevel(claimed)

    const id = layerSeq++
    floaterLayers.push({ id, z: modalContentZ(claimed), aboveFloater: openedFromFloaterPanel() })
    syncFloaterZ()

    return () => {
      const index = claimedLevels.indexOf(claimed)
      if (index !== -1) claimedLevels.splice(index, 1)
      const layer = floaterLayers.findIndex((item) => item.id === id)
      if (layer !== -1) floaterLayers.splice(layer, 1)
      syncFloaterZ()
    }
  }, [isOpen, parentLevel])

  return level
}
